import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { collect } from 'collect.js';
import { Form, Input, Select } from 'antd';
import { IMPORTABLE_MENU_PROVIDERS, MenuProvider } from '../../services/exports/Constants';
import CompanySelect from '../form/business/CompanySelect';
import {
  DeliverooLinkError,
  DoorDashLinkError,
  GloriaFoodLinkError,
  getDeliverooLinkError,
  getDoorDashLinkError,
  getGloriaFoodLinkError,
  normalizeGloriaFoodLink,
} from './restaurantLinkValidation';
import { Stringable } from '../../services/helpers/Stringable';

const GLORIA_FOOD_LINK_ERROR_KEYS = {
  [GloriaFoodLinkError.InvalidLink]: 'form.provider_id.gloria_food.error',
  [GloriaFoodLinkError.BackOffice]: 'form.provider_id.gloria_food.back_office_error',
  [GloriaFoodLinkError.WrongPlatform]: 'form.provider_id.gloria_food.wrong_platform_error',
};

const DOORDASH_LINK_ERROR_KEYS = {
  [DoorDashLinkError.NotStorePage]: 'form.provider_id.doordash.error',
  [DoorDashLinkError.StoreNumberOnly]: 'form.provider_id.doordash.store_number_error',
};

const DELIVEROO_LINK_ERROR_KEYS = {
  [DeliverooLinkError.NotMenuPage]: 'form.provider_id.deliveroo.error',
};

// The two links a GloriaFood restaurant can have, in the order the helper lists them.
const GLORIA_FOOD_LINK_TYPES = ['ordering_page', 'embedded_widget'];

export default function ImportMenu({ data, update, nullable, className }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Menu:ImportMenu' });

  const isGloriaFood = data.provider === MenuProvider.GloriaFood;
  const isDoorDash = data.provider === MenuProvider.DoorDash;
  const isDeliveroo = data.provider === MenuProvider.Deliveroo;

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const company = useSelector((store) => store.currentCompany.company);

  const DefaultInput = useMemo(
    () => (
      <Input
        value={data.provider_id ?? ''}
        onChange={(e) => update('provider_id', e.target.value)}
        onBlur={() => {
          // A bare GloriaFood restaurant UID is shown as the link it imports as.
          const link = isGloriaFood ? normalizeGloriaFoodLink(data.provider_id) : data.provider_id;

          if (link !== data.provider_id) {
            update('provider_id', link);
          }
        }}
        placeholder={t(`form.provider_id.${data.provider}.placeholder`, { defaultValue: '' })}
      />
    ),
    [data.provider, data.provider_id, isGloriaFood],
  );

  const ProviderIdInput = useMemo(
    () =>
      ({
        [MenuProvider.Foodamigos]: (
          <CompanySelect
            value={data.provider_id}
            except={[company?.id]}
            onChange={(value) => update('provider_id', value)}
          />
        ),
      })[data.provider] ?? DefaultInput,
    [data.provider, data.provider_id, DefaultInput],
  );

  const options = useMemo(
    () =>
      // Regional configs may list providers without an import pipeline —
      // offering those would only end in a backend validation error, so the
      // dropdown is limited to importable ones.
      collect([...(serviceProvider?.regional_config?.supported_menu_providers ?? [])])
        .filter((item) => IMPORTABLE_MENU_PROVIDERS.includes(item))
        .when(nullable, (collection) => collection.prepend(null))
        // A regional_config can list a provider this bundle has no label for yet — humanise the
        // key instead of rendering it raw.
        .map((item) => ({
          label: t(`form.provider.options.${item}`, { defaultValue: item ? Stringable.toHuman(item) : undefined }),
          value: item,
        }))
        .toArray(),
    [serviceProvider?.regional_config?.supported_menu_providers, nullable],
  );

  // Client-side link validation for GloriaFood: the import takes the URL of
  // the restaurant's ordering page (foodbooking.com, a white-label domain, or
  // the restaurant's own website with the embedded widget) — the scraper
  // resolves the restaurant itself, so the host cannot be whitelisted. What can
  // be caught is a back-office link or another platform's page (PROD-521).
  // DoorDash takes a doordash.com store page only, Deliveroo a restaurant menu
  // page only. Only surface an error once the user has typed something so an
  // empty field isn't pre-flagged.
  const linkErrorKey = useMemo(() => {
    if (!data.provider_id) {
      return null;
    }

    if (isGloriaFood) {
      return GLORIA_FOOD_LINK_ERROR_KEYS[getGloriaFoodLinkError(data.provider_id)] ?? null;
    }

    if (isDoorDash) {
      return DOORDASH_LINK_ERROR_KEYS[getDoorDashLinkError(data.provider_id)] ?? null;
    }

    if (isDeliveroo) {
      return DELIVEROO_LINK_ERROR_KEYS[getDeliverooLinkError(data.provider_id)] ?? null;
    }

    return null;
  }, [isGloriaFood, isDoorDash, isDeliveroo, data.provider_id]);

  // Which of the two GloriaFood links to enter, kept visible under an error so
  // the partner can see what the right one looks like.
  const gloriaFoodLinkHelp = (
    <ul className="mt-2xs mb-0 flex list-disc flex-col gap-xs pl-md" data-testid="gloria-food-link-help">
      {GLORIA_FOOD_LINK_TYPES.map((type) => (
        <li key={type}>
          <span className="font-semibold">{t(`form.provider_id.gloria_food.help.${type}.title`)}</span>{' '}
          {t(`form.provider_id.gloria_food.help.${type}.description`)}
          <code className="mt-2xs block break-all text-xs">
            {t(`form.provider_id.gloria_food.help.${type}.example`)}
          </code>
        </li>
      ))}
    </ul>
  );

  const linkErrorProps = {
    validateStatus: linkErrorKey ? 'error' : undefined,
    help: linkErrorKey ? t(linkErrorKey) : undefined,
  };

  const providerIdFieldProps =
    {
      [MenuProvider.GloriaFood]: { extra: gloriaFoodLinkHelp, ...linkErrorProps },
      [MenuProvider.DoorDash]: { extra: t('form.provider_id.doordash.help'), ...linkErrorProps },
      [MenuProvider.Deliveroo]: { extra: t('form.provider_id.deliveroo.help'), ...linkErrorProps },
    }[data.provider] ?? {};

  return (
    <div className={className}>
      <Form.Item label={t('form.provider.label')} required>
        <Select options={options} value={data.provider} onChange={(value) => update('provider', value)} />
      </Form.Item>
      {!!data.provider && (
        <Form.Item
          label={t(`form.provider_id.${data.provider}.label`, {
            defaultValue: `${Stringable.toHuman(data.provider)} URL`,
          })}
          required
          {...providerIdFieldProps}
        >
          {ProviderIdInput}
        </Form.Item>
      )}
    </div>
  );
}
